import React from "react";
import {Alert, Drawer, IconButton, Skeleton} from "@mui/material";
import {Close} from "@mui/icons-material";
import type {GridColDef} from "@mui/x-data-grid-premium";
import {CustomDataGrid} from "~/components/data-grid/CustomDataGrid.tsx";
import {Tag} from "~/components/tag/Tag.tsx";
import type {OpportunityRow} from "../contractExplorerTypes.ts";
import {KIND_LABEL} from "../contractExplorerTypes.ts";
import {
    evidenceColumns,
    evidenceValue,
    formatDate,
    formatMoney,
    formatNumber,
    formatValue,
    opportunityFigure,
    typeLabel,
} from "../contractExplorerFormat.ts";
import {useContractOpportunityQuery} from "../useContractOpportunitiesQuery.ts";
import {CollapsibleSection} from "./CollapsibleSection.tsx";
import {ConfidenceBadge} from "./ConfidenceBadge.tsx";
import {KeyValue} from "./KeyValue.tsx";
import {Markdown} from "./Markdown.tsx";
import {Section} from "./Section.tsx";

type Props = {
    bagId: number;
    contractId: number;
    opportunityId: number | null;
    onClose: () => void;
};

// How the specialist found it, shown apart from the numbers that explain it.
const HOW = ['rows_where', 'table', 'queries'];

/** A fact as text: an object (a breakdown by entity, say) as JSON, the rest as the page formats values. */
const factText = (value: unknown): string =>
    value !== null && typeof value === 'object' && !Array.isArray(value) ? JSON.stringify(value) : formatValue(value);

/** One finding: the specialist's analysis and recommended action, its numbers, and the evidence on both sides. */
export const OpportunityDetail: React.FC<Props> = ({bagId, contractId, opportunityId, onClose}) => {
    const {data: found, isPending, error} = useContractOpportunityQuery(bagId, contractId, opportunityId);
    const currency = found?.currency ?? '';

    // The kind's own columns, between the transaction and its spend: what its saving was computed from.
    const columns = React.useMemo<GridColDef<OpportunityRow>[]>(() => {
        const {columns: own, others} = found ? evidenceColumns(found.kind, found.rows) : {columns: [], others: []};
        return [
            {field: 'role', headerName: 'Shows', width: 130, renderCell: ({row}) => typeLabel(row.role)},
            {field: 'p_id', headerName: 'Transaction', minWidth: 200, flex: 1.4,
                renderCell: ({row}) => (
                    <span className="ci-cname">
                        <b title={row.description}>{row.description || row.p_id}</b>
                        <span className="ci-csub"><span className="ci-id">#{row.p_id}</span>{row.supplier && `· ${row.supplier}`}</span>
                    </span>
                )},
            {field: 'date', headerName: 'Date', width: 120, renderCell: ({row}) => formatDate(row.date)},
            ...own.map((c): GridColDef<OpportunityRow> => ({
                field: `fact:${c.key}`, headerName: c.label, width: c.numeric ? 120 : 150, sortable: false,
                align: c.numeric ? 'right' : undefined, headerAlign: c.numeric ? 'right' : undefined,
                renderCell: ({row}) => {
                    const value = evidenceValue(c.key, row.facts ?? {});
                    return c.numeric ? <span className="ci-mono">{formatNumber(value)}</span> : formatValue(value);
                },
            })),
            ...(others.length ? [{
                field: 'others', headerName: 'Other facts', minWidth: 160, flex: 1, sortable: false,
                renderCell: ({row}) => others.filter(k => row.facts?.[k] !== undefined)
                    .map(k => `${typeLabel(k)}: ${factText(row.facts[k])}`).join('; '),
            } satisfies GridColDef<OpportunityRow>] : []),
            {field: 'amount', headerName: currency ? `Spend (${currency})` : 'Spend', width: 120, align: 'right',
                headerAlign: 'right', renderCell: ({row}) => <span className="ci-mono">{formatMoney(row.amount ?? row.spend)}</span>},
            ...(found?.category === 'leakage' ? [{
                field: 'saving', headerName: currency ? `Saving (${currency})` : 'Saving', width: 120, align: 'right',
                headerAlign: 'right', renderCell: ({row}) => <span className="ci-mono">{formatMoney(row.saving)}</span>,
            } satisfies GridColDef<OpportunityRow>] : []),
        ];
    }, [found, currency]);

    let body: React.ReactNode;
    if (error) body = <Alert severity="error">This opportunity could not be loaded: {error.message}</Alert>;
    else if (isPending || !found) body = <Skeleton variant="rounded" height={320}/>;
    else {
        const figure = opportunityFigure(found);
        const queries = Array.isArray(found.facts.queries) ? found.facts.queries as string[] : [];
        body = <>
            <header className="ci-odhead">
                <Tag label={KIND_LABEL[found.kind]} color={found.category === 'leakage' ? 'success' : 'warning'}/>
                <h2 className="ci-dtitle">{formatMoney(figure.value, currency)} <span className="ci-sub">{figure.label.toLowerCase()}</span></h2>
                <div className="ci-dmeta">
                    <b>{found.supplier || 'No supplier'}</b>
                    <span className="sep">·</span>
                    <span>{formatDate(found.period_from)} → {formatDate(found.period_to)}</span>
                    <span className="sep">·</span>
                    <ConfidenceBadge value={found.confidence} title="The specialist's confidence in this finding"/>
                </div>
                {found.summary && <p className="ci-osummary">{found.summary}</p>}
            </header>
            {found.warnings.map(w => <Alert key={w} severity="warning">{w}</Alert>)}
            <Section title="Analysis"><Markdown text={found.analysis}/></Section>
            <Section title="Recommended action"><Markdown text={found.recommended_action}/></Section>
            <Section title="Facts">
                <div className="ci-kv">
                    {Object.entries(found.facts).filter(([k]) => !HOW.includes(k)).map(([k, v]) => (
                        <KeyValue key={k} label={typeLabel(k)}>{factText(v)}</KeyValue>
                    ))}
                </div>
            </Section>
            <Section title={`Contract evidence (${found.contract_evidence.length})`}
                     sub={found.contract_evidence.length ? undefined : "Measured against the contract's scope: its supplier, entities and period."}>
                {found.contract_evidence.map(e => (
                    <KeyValue key={`${e.role}:${e.ref}`} label={`${typeLabel(e.role)} · ${e.field ?? ''}`}>
                        {formatValue(e.value)} <span className="ci-csub">{e.file}</span>
                    </KeyValue>
                ))}
            </Section>
            <Section title={`Transactions (${found.rows.length})`}>
                <div className="ci-grid">
                    <CustomDataGrid rows={found.rows} getRowId={row => `${row.role}:${row.p_id}`} columns={columns} autoHeight
                                    rowSize="small" hideFooter={found.rows.length <= 25} pageSizeOptions={[25, 50]}
                                    disableRowSelectionOnClick/>
                </div>
            </Section>
            {queries.length > 0 && (
                <CollapsibleSection title={`How it was found (${queries.length} queries)`}
                                    sub="The specialist's queries over this contract's evidence, in order.">
                    {queries.map((q, i) => <code key={i} className="ci-query">{q}</code>)}
                </CollapsibleSection>
            )}
        </>;
    }
    return (
        <Drawer anchor="right" open={opportunityId !== null} onClose={onClose} classes={{paper: 'ci-odrawer'}}>
            <div className="contract-explorer-page ci ci-odrawer-body">
                <IconButton className="ci-odclose" onClick={onClose} aria-label="Close"><Close/></IconButton>
                {body}
            </div>
        </Drawer>
    );
};
